import { getPublicFileFromS3 } from '@/lib/s3';
import * as Post from '@/redux/post/post.d';

export function filterAttachments(
  attachments: Post.IPostAttachment[] | undefined
) {
  if (!attachments) return [];

  return attachments.filter((attachment) => {
    if (
      (attachment.resourceType === Post.IPostAttachmentTypes.ZIP &&
        attachment.category === Post.IAttachmentCategory.postZip) ||
      (attachment.resourceType === Post.IPostAttachmentTypes.YOUTUBE &&
        attachment.category === Post.IAttachmentCategory.postMedia)
    ) {
      return false;
    }
    return true;
  });
}

export function filterAttachmentsForCarousel(
  attachments: Post.IPostAttachment[] | undefined
) {
  if (!attachments) return [];

  return attachments.filter((attachment) => {
    if (
      attachment?.resourceType === Post.IPostAttachmentTypes.ZIP &&
      attachment?.category === Post.IAttachmentCategory.postZip
    ) {
      return false;
    }
    return true;
  });
}

export const getGroupedOptionsFlat = (
  groupedEnum: Post.GroupedEnum
): string[] => {
  const flatOptions: string[] = Object.entries(groupedEnum).flatMap(
    ([materialGroupKey, materialEnum]) => {
      const option: string[] = Object.entries(
        materialEnum as Post.EnumType
      ).map(
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        ([_, value]) => value
      );
      if (materialGroupKey === '[object Object]') {
        return [''];
      }
      return option;
    }
  );

  return flatOptions;
};

export const getGroupedOptionsFromEnumAsGroups = (
  groupedEnum: Post.GroupedEnum
): Post.IGroupedOption[] => {
  const groupedOptions: Post.IGroupedOption[] = Object.entries(groupedEnum).map(
    ([materialGroupKey, materialEnum]) => {
      const options: Post.IOption[] = Object.entries(
        materialEnum as Post.EnumType
      ).map(([key, value]) => ({
        value: key,
        label: value,
      }));
      if (materialGroupKey === '[object Object]')
        return { label: '', options: [] };

      return {
        label:
          materialGroupKey.charAt(0).toUpperCase() +
          materialGroupKey.slice(1).toLowerCase(),
        options,
      };
    }
  );

  return groupedOptions;
};

export async function urlToFile(
  url: string,
  filename: string
): Promise<File | null> {
  if (typeof url !== 'string' || url.trim() === '') {
    throw new Error('The URL must be a valid string.');
  }

  if (typeof filename !== 'string' || filename.trim() === '') {
    throw new Error('The filename must be a valid string.');
  }

  try {
    const response = await getPublicFileFromS3(url);

    const contentType = response.headers.get('Content-Type');

    const blob = await response.blob();
    const type = contentType || 'application/octet-stream';
    return new File([blob], filename, { type });
  } catch (error) {
    return null;
  }
}

async function getFilesByCategory(
  attachments: Post.IPostAttachment[],
  category: Post.IAttachmentCategory
): Promise<File[]> {
  const filteredAttachments = attachments.filter(
    (attachment) =>
      attachment.category === category &&
      attachment.resourceType !== Post.IPostAttachmentTypes.YOUTUBE
  );

  const files = await Promise.all(
    filteredAttachments.map((attachment) =>
      urlToFile(attachment.url, attachment.fileName)
    )
  );

  return files.filter((file) => file !== null);
}

/**
 * Converts the response from the backend to a PostUploadData
 */
export async function convertToPostUploadData(
  dto: Post.IPostDetailResponse
): Promise<Post.IPostUploadData> {
  const thumbnailFile = await getFilesByCategory(
    dto.attachments,
    Post.IAttachmentCategory.postThumbnail
  );

  const mediaFiles = await getFilesByCategory(
    dto.attachments,
    Post.IAttachmentCategory.postMedia
  );

  const modelAttachments = dto.attachments.filter((attachment) => {
    if (
      attachment.category !== Post.IAttachmentCategory.postModel &&
      attachment.resourceType !== Post.IPostAttachmentTypes.MODEL
    )
      return false;

    return true;
  });

  const models = (
    await Promise.all(
      filterAttachments(modelAttachments).map(async (attachment) => {
        const modelFile = await urlToFile(attachment.url, attachment.fileName);
        if (!modelFile) return null;

        return {
          id: attachment._id,
          modelURL: attachment.url,
          modelFile,
        };
      })
    )
  ).filter((model) => model !== null);

  const youtubeAttachments = dto.attachments
    .filter((attachment) => {
      if (
        attachment.category === Post.IAttachmentCategory.postMedia &&
        attachment.resourceType === Post.IPostAttachmentTypes.YOUTUBE
      ) {
        return true;
      }

      return false;
    })
    .map((attachment) => attachment.url);

  const isRotary = dto.jobType === Post.IJobType.ROTARY;

  if (isRotary) {
    return {
      title: dto.title,
      description: dto.description,
      category: dto.category?._id || '',
      tags: dto.tags.map((tag) => tag._id),
      onefinityModel: dto.onefinityModel as Post.IOnefinityModel,
      onefinityController: dto.onefinityController as Post.IOnefinityController,
      onefinitySize: dto.onefinitySize as Post.IOnefinitySize,
      softwareUsed: dto.softwareUsed as Post.ISoftwareUsed,
      materialUsed: dto.materialUsed as unknown as Post.IMaterialUsed,
      jobType: dto.jobType as Post.IJobType,
      units: dto.units as Post.IUnits,
      jobSizeDiameter: dto.jobSize?.diameter || 0.1,
      jobSizeLength: dto.jobSize?.length || 0.1,
      zZeroPosition: dto.zZeroPosition as Post.IZZeroPosition,
      xyDatumPosition: dto.xyDatumPosition as Post.IXYDatumPosition,
      laserOrEndmill: dto.laserOrEndmill as Post.ILaserOrEndmill,
      toolData: dto.toolData,
      toolSetter: dto.toolSetter as Post.IToolSetter,
      probeForZHeightCommand:
        dto.probeForZHeightCommand as Post.IProbeForZHeightCommand,
      spindleSpeed: dto.spindleSpeed,
      models,
      mediaFiles,
      thumbnail: thumbnailFile[0] || null,
      youtubeAttachments,
      supportedMachines: dto.supportedMachines,
      originalMachine: dto.originalMachine,
      licenceType: dto.licenceType as Post.ILicense,
    } as Post.IPostUploadData;
  }

  return {
    title: dto.title,
    description: dto.description,
    category: dto.category?._id || '',
    tags: dto.tags.map((tag) => tag._id),
    onefinityModel: dto.onefinityModel as Post.IOnefinityModel,
    onefinityController: dto.onefinityController as Post.IOnefinityController,
    onefinitySize: dto.onefinitySize as Post.IOnefinitySize,
    softwareUsed: dto.softwareUsed as Post.ISoftwareUsed,
    materialUsed: dto.materialUsed as unknown as Post.IMaterialUsed,
    jobType: dto.jobType as Post.IJobType,
    units: dto.units as Post.IUnits,
    jobSizeHeight: dto.jobSize?.height || 0.1,
    jobSizeThickness: dto.jobSize?.thickness || 0.1,
    jobSizeWidth: dto.jobSize?.width || 0.1,
    zZeroPosition: dto.zZeroPosition as Post.IZZeroPosition,
    xyDatumPosition: dto.xyDatumPosition as Post.IXYDatumPosition,
    laserOrEndmill: dto.laserOrEndmill as Post.ILaserOrEndmill,
    toolData: dto.toolData,
    toolSetter: dto.toolSetter as Post.IToolSetter,
    probeForZHeightCommand:
      dto.probeForZHeightCommand as Post.IProbeForZHeightCommand,
    spindleSpeed: dto.spindleSpeed,
    models,
    mediaFiles,
    thumbnail: thumbnailFile[0] || null,
    youtubeAttachments,
    supportedMachines: dto.supportedMachines,
    originalMachine: dto.originalMachine,
    licenceType: dto.licenceType as Post.ILicense,
  } as Post.IPostUploadData;
}

/**
 * Converts data for sending to the backend
 */
export function convertToPostDetailData(
  dto: Post.IPostUploadData
): Post.IPostDataUploadRequest {
  const toolData = dto.toolData.map((tool) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { _id, ...rest } = tool;
    return {
      ...rest,
    };
  });

  const isRotary = dto.jobType === Post.IJobType.ROTARY;

  const result = {
    title: dto.title,
    description: dto.description,
    category: dto.category,
    tags: dto.tags,
    onefinityModel: dto.onefinityModel,
    onefinityController: dto.onefinityController,
    onefinitySize: dto.onefinitySize,
    softwareUsed: dto.softwareUsed,
    materialUsed: dto.materialUsed,
    jobType: dto.jobType,
    jobSize: isRotary
      ? {
          diameter: dto.jobSizeDiameter,
          length: dto.jobSizeLength,
        }
      : {
          height: dto.jobSizeHeight,
          width: dto.jobSizeWidth,
          thickness: dto.jobSizeThickness,
        },
    laserOrEndmill: dto.laserOrEndmill,
    probeForZHeightCommand: dto.probeForZHeightCommand,
    spindleSpeed: dto.spindleSpeed,
    toolSetter: dto.toolSetter,
    units: dto.units,
    xyDatumPosition: dto.xyDatumPosition,
    zZeroPosition: dto.zZeroPosition,
    youtubeAttachments: dto.youtubeAttachments,
    toolData,
    originalMachine: dto.originalMachine,
    supportedMachines: dto.supportedMachines,
    licenceType: dto.licenceType,
  };

  return Object.fromEntries(
    Object.entries(result).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
      ([key, value]) =>
        value !== undefined &&
        value !== null &&
        (typeof value !== 'string' || value.trim() !== '') &&
        !(
          Array.isArray(value) &&
          value.length === 0 &&
          key !== 'supportedMachines'
        )
    )
  ) as Post.IPostDataUploadRequest;
}

export function convertToPostDetailAttachment(
  dto: Post.IPostUploadData
): Post.IPostAttachmentUploadRequest {
  const data: Post.IPostAttachmentUploadRequest = {};

  if (dto.thumbnail instanceof File) {
    data.thumbnail = dto.thumbnail;
  }

  if (dto.models.length > 0) {
    data.modelFiles = dto.models.map((model) => model.modelFile);
  }

  if (dto.mediaFiles.length > 0) {
    data.mediaFiles = dto.mediaFiles;
  }

  return data;
}
